// src/pages/shared/NotificationInboxPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Bell, Check, CheckCheck, Trash2, ExternalLink, Settings,
  Inbox, Filter, ChevronLeft, ChevronRight, Clock,
  ShoppingBag, CreditCard, XCircle, AlertTriangle,
  Package, Truck, Star, Shield, Tag,
} from 'lucide-react'
import { notificationsApi } from '@/api/services'
import { Card, CardBody, Button, Badge, PageSpinner, Empty } from '@/components/common/ui'

const TYPE_CONFIG = {
  order_placed:       { icon: ShoppingBag, color: 'text-pink',       bg: 'bg-pink/10' },
  order_confirmed:    { icon: Check,       color: 'text-green-600',  bg: 'bg-green-50' },
  order_processing:   { icon: Clock,       color: 'text-amber-600',  bg: 'bg-amber-50' },
  order_shipped:      { icon: Truck,       color: 'text-blue-600',   bg: 'bg-blue-50' },
  order_delivered:    { icon: Package,     color: 'text-green-600',  bg: 'bg-green-50' },
  order_cancelled:    { icon: XCircle,     color: 'text-red-600',    bg: 'bg-red-50' },
  payment_success:    { icon: CreditCard,  color: 'text-green-600',  bg: 'bg-green-50' },
  payment_failed:     { icon: AlertTriangle, color: 'text-red-600',  bg: 'bg-red-50' },
  refund_requested:   { icon: CreditCard,  color: 'text-amber-600',  bg: 'bg-amber-50' },
  refund_processed:   { icon: CreditCard,  color: 'text-green-600',  bg: 'bg-green-50' },
  review_received:    { icon: Star,        color: 'text-yellow-500', bg: 'bg-yellow-50' },
  review_approved:    { icon: Star,        color: 'text-green-600',  bg: 'bg-green-50' },
  payout_initiated:   { icon: CreditCard,  color: 'text-blue-600',   bg: 'bg-blue-50' },
  payout_completed:   { icon: CreditCard,  color: 'text-green-600',  bg: 'bg-green-50' },
  low_stock:          { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50' },
  product_approved:   { icon: Tag,         color: 'text-green-600',  bg: 'bg-green-50' },
  product_rejected:   { icon: XCircle,     color: 'text-red-600',    bg: 'bg-red-50' },
  supplier_approved:  { icon: Shield,      color: 'text-green-600',  bg: 'bg-green-50' },
  default:            { icon: Bell,        color: 'text-pink',       bg: 'bg-pink/10' },
}

const PRIORITY_BORDER = {
  urgent: 'border-red-300 bg-red-50/30',
  high:   'border-amber-300 bg-amber-50/20',
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer p-3 bg-surface rounded-xl">
      <button type="button" onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-pink' : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
      </button>
      <span className="text-sm font-semibold text-gray-700">{label}</span>
    </label>
  )
}

export default function NotificationInboxPage() {
  const [tab, setTab] = useState('inbox')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)
  const qc = useQueryClient()

  // ── Queries ──────────────────────────────────────────────────────
  const { data: notifsData, isLoading } = useQuery({
    queryKey: ['notifications', filter, page],
    queryFn: () => notificationsApi.list({
      per_page: 20, page,
      ...(filter === 'unread' ? { unread_only: 1 } : {}),
    }).then(r => r.data),
  })

  const { data: countData } = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => notificationsApi.unreadCount().then(r => r.data),
    refetchInterval: 30000,
  })

  const { data: prefsData, isLoading: prefsLoading } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => notificationsApi.preferences().then(r => r.data),
    enabled: tab === 'prefs',
  })

  // ── Mutations ────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['notifications'] })
    qc.invalidateQueries({ queryKey: ['unread-count'] })
  }

  const markReadMut = useMutation({
    mutationFn: (id) => notificationsApi.markRead(id),
    onSuccess: invalidate,
  })

  const markAllMut = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: invalidate,
  })

  const deleteMut = useMutation({
    mutationFn: (id) => notificationsApi.remove(id),
    onSuccess: invalidate,
  })

  const savePrefsMut = useMutation({
    mutationFn: (d) => notificationsApi.updatePrefs(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-prefs'] }),
  })

  const notifications = notifsData?.data?.data ?? notifsData?.data ?? []
  const lastPage = notifsData?.data?.last_page ?? notifsData?.last_page ?? 1
  const unread = countData?.unread_count ?? countData?.count ?? 0

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-display font-bold flex items-center gap-2">
            <Bell size={20} className="text-pink" /> Notifications
            {unread > 0 && (
              <span className="bg-pink text-white rounded-full px-2.5 py-0.5 text-xs font-extrabold">
                {unread} unread
              </span>
            )}
          </h1>
          <p className="text-sm text-muted mt-0.5">Your activity feed and alerts</p>
        </div>
        {unread > 0 && tab === 'inbox' && (
          <Button variant="secondary" size="sm" onClick={() => markAllMut.mutate()} loading={markAllMut.isPending}>
            <CheckCheck size={14} /> Mark all read
          </Button>
        )}
      </div>

      {/* Tab bar */}
      <div className="bg-white border border-border rounded-2xl p-3 mb-5">
        <div className="flex items-center gap-1">
          {[
            { key: 'inbox', label: 'Inbox', icon: Inbox },
            { key: 'prefs', label: 'Preferences', icon: Settings },
          ].map(t => {
            const Icon = t.icon
            const isActive = tab === t.key
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  isActive ? 'bg-pink text-white' : 'text-muted hover:bg-surface'
                }`}>
                <Icon size={13} /> {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── INBOX ── */}
      {tab === 'inbox' && (
        <>
          {/* Filter chips */}
          <div className="flex items-center gap-2 mb-4">
            <Filter size={14} className="text-muted" />
            {[['all', 'All'], ['unread', 'Unread only']].map(([k, l]) => (
              <button key={k} onClick={() => { setFilter(k); setPage(1) }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                  filter === k
                    ? 'border-pink bg-pink text-white'
                    : 'border-border bg-white text-muted hover:border-pink/30'
                }`}>
                {l}
              </button>
            ))}
          </div>

          {isLoading && <PageSpinner />}

          {!isLoading && notifications.length === 0 && (
            <Empty
              icon={filter === 'unread' ? '🎉' : '🔔'}
              title={filter === 'unread' ? 'All caught up!' : 'No notifications yet'}
              description={filter === 'unread' ? 'You have no unread notifications.' : 'Activity will appear here as it happens.'}
            />
          )}

          {!isLoading && notifications.length > 0 && (
            <div className="space-y-2.5">
              {notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.default
                const Icon = cfg.icon
                const priorityClass = PRIORITY_BORDER[n.priority] || ''
                return (
                  <Card key={n.id} className={`transition-all ${!n.is_read ? priorityClass : 'opacity-75'}`}>
                    <CardBody className="!p-4">
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon size={16} className={cfg.color} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm ${n.is_read ? 'font-semibold text-gray-700' : 'font-bold text-gray-900'}`}>
                                {n.title}
                              </span>
                              {!n.is_read && (
                                <span className="w-2 h-2 rounded-full bg-pink flex-shrink-0" />
                              )}
                              {n.priority && n.priority !== 'normal' && (
                                <Badge className={
                                  n.priority === 'urgent' ? 'bg-red-100 text-red-700 text-[10px]' :
                                  n.priority === 'high' ? 'bg-amber-100 text-amber-700 text-[10px]' :
                                  'bg-gray-100 text-gray-500 text-[10px]'
                                }>
                                  {n.priority}
                                </Badge>
                              )}
                            </div>
                            <span className="text-[11px] text-muted whitespace-nowrap flex-shrink-0">
                              {n.created_at && new Date(n.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          </div>

                          <p className="text-xs text-gray-500 leading-relaxed mb-2">{n.body}</p>

                          <div className="flex items-center gap-2 flex-wrap">
                            {n.action_url && (
                              <a href={n.action_url}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-pink text-white text-[11px] font-bold hover:opacity-90 transition-opacity">
                                {n.action_label || 'View'} <ExternalLink size={10} />
                              </a>
                            )}
                            {!n.is_read && (
                              <button onClick={() => markReadMut.mutate(n.id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-border text-[11px] font-semibold text-muted hover:border-pink hover:text-pink transition-colors">
                                <Check size={10} /> Mark read
                              </button>
                            )}
                            <button onClick={() => deleteMut.mutate(n.id)}
                              className="ml-auto text-gray-300 hover:text-red-400 transition-colors p-1">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          )}

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-xs font-bold disabled:opacity-40 hover:border-pink transition-colors">
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="text-xs text-muted font-semibold">Page {page} of {lastPage}</span>
              <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page === lastPage}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-xs font-bold disabled:opacity-40 hover:border-pink transition-colors">
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* ── PREFERENCES ── */}
      {tab === 'prefs' && (
        prefsLoading ? <PageSpinner /> : <PreferencesPanel prefs={prefsData?.data ?? prefsData} onSave={d => savePrefsMut.mutate(d)} saving={savePrefsMut.isPending} />
      )}
    </div>
  )
}

function PreferencesPanel({ prefs, onSave, saving }) {
  const [form, setForm] = useState(prefs || {})
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  return (
    <Card>
      <CardBody>
        <h3 className="text-base font-display font-bold text-gray-900 mb-5">Notification Preferences</h3>

        {/* Channel toggles */}
        <p className="text-xs font-bold text-muted uppercase tracking-wider mb-3">Channels</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Toggle checked={form.in_app_enabled ?? true} onChange={v => set('in_app_enabled', v)} label="In-App Inbox" />
          <Toggle checked={form.email_enabled ?? true} onChange={v => set('email_enabled', v)} label="Email" />
          <Toggle checked={form.whatsapp_enabled ?? false} onChange={v => set('whatsapp_enabled', v)} label="WhatsApp" />
          <Toggle checked={form.digest_enabled ?? false} onChange={v => set('digest_enabled', v)} label="Daily Digest" />
        </div>

        {/* Digest time */}
        {form.digest_enabled && (
          <div className="mb-6">
            <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Digest Time</label>
            <input type="time" value={form.digest_time ?? '09:00'} onChange={e => set('digest_time', e.target.value)}
              className="input w-40" />
          </div>
        )}

        {/* Quiet hours */}
        <p className="text-xs font-bold text-muted uppercase tracking-wider mb-3">Quiet Hours</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div>
            <label className="block text-[11px] text-muted font-semibold mb-1">Start</label>
            <input type="time" value={form.quiet_hours_start ?? ''} onChange={e => set('quiet_hours_start', e.target.value)}
              className="input w-full" />
          </div>
          <div>
            <label className="block text-[11px] text-muted font-semibold mb-1">End</label>
            <input type="time" value={form.quiet_hours_end ?? ''} onChange={e => set('quiet_hours_end', e.target.value)}
              className="input w-full" />
          </div>
        </div>

        <Button onClick={() => onSave(form)} loading={saving}>Save Preferences</Button>
      </CardBody>
    </Card>
  )
}
